import React, { FC, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { endOfMonth, format, nowDate, startOfMonth, yesterDay } from '../../../utils/date'
import COLOR_LIST from '../../constants/taskColor'
import SearchModule from './SearchModule'

interface Props {
  name: string
  date?: string
  teamIds?: number[]
  memberIds?: number[]
  keywords?: string[]
  labelMasterKeywords?: string[]
  sortKey?: string
  sortDirection?: string
  disabled?: boolean
  labelGroupIds?: number[]
  masterRelationLabelGroupIds?: number[]
  masterRelationLabelGroupDisabled?: boolean
  colors?: string[]
  creatorMemberIds?: number[]
  viewerMemberIds?: number[]
  userMemberIds?: number[]
  callbackTeamIds?: (teamIds: number[]) => void
  callbackMemberIds?: (memberIds: number[]) => void
  callbackKeywords?: (keywords: string[]) => void
  callbackLabelMasterKeywords?: (labelMasterKeywords: string[]) => void
  callbackKey?: (key: string) => void
  callbackDirection?: (direction: string) => void
  callbackDisabled?: (disabled: boolean) => void
  callbackPageOffset?: (page: number) => void
  callbackLabelGroupIds?: (projectIds: number[]) => void
  callbackMasterRelationLabelGroupIds?: (projectIds: number[]) => void
  callbackMasterRelationLabelGroupDisabled?: (disabled: boolean) => void
  callbackColorLabels?: (colorLabels: string[]) => void
  callbackCreatorMemberIds?: (memberIds: number[]) => void
  callbackViewerMemberIds?: (memberIds: number[]) => void
  callbackUserMemberIds?: (memberIds: number[]) => void
  memberList?: { id: number, name: string }[]
  teamList?: { id: number, name: string }[]
  labelGroupList?: { id: number, name: string }[]
  masterRelationLabelGroupList?: { id: number, name: string }[]
}

const SearchConditionModule: FC<Props> = (props) => {
  const {
    name,
    date,
    teamIds,
    memberIds,
    keywords,
    labelMasterKeywords,
    sortKey,
    sortDirection,
    disabled,
    labelGroupIds,
    masterRelationLabelGroupIds,
    masterRelationLabelGroupDisabled,
    colors,
    creatorMemberIds,
    viewerMemberIds,
    userMemberIds,
    callbackTeamIds,
    callbackMemberIds,
    callbackKeywords,
    callbackLabelMasterKeywords,
    callbackKey,
    callbackDirection,
    callbackDisabled,
    callbackPageOffset,
    callbackLabelGroupIds,
    callbackMasterRelationLabelGroupIds,
    callbackMasterRelationLabelGroupDisabled,
    callbackColorLabels,
    callbackCreatorMemberIds,
    callbackViewerMemberIds,
    callbackUserMemberIds,
    memberList,
    teamList,
    labelGroupList,
    masterRelationLabelGroupList,
  } = props

  const {
    login,
  } = useSelector((state: RootState) => state)

  const endDate = useMemo(() => {
    if (!date) return ""
    if (Number(format(date, "YYYYMM")) === Number(nowDate("YYYYMM"))) {
      return yesterDay("YYYY-MM-DD")
    }
    return endOfMonth(date, "YYYY-MM-DD")
  }, [date])

  const [isDisplaySticky, setIsDisplaySticky] = useState(false)

  const scrollTop = (): number => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)

  const onScroll = (): void => {
    if (scrollTop() <= 400) {
      setIsDisplaySticky(false)
    } else {
      setIsDisplaySticky(true)
    }
  }

  const buildSortValue = () => {
    let KeyName = ""
    switch (sortKey) {
      case "id":
        KeyName = "プロジェクト作成日"
        break
      case "scheduled_start_date":
        KeyName = "プロジェクト開始日"
        break
      case "scheduled_end_date":
        KeyName = "プロジェクト終了予定日"
        break
      case "disabled_at":
        KeyName = "プロジェクト完了日"
        break
      case "name":
        KeyName = "プロジェクト名"
        break
      case "code":
        KeyName = "プロジェクトコード"
        break
      case "order_index":
        KeyName = "カスタム設定"
        break
      default:
        break
    }
    const DirectionName = sortDirection === "asc" ? "昇順" : "降順"
    return `${KeyName}【${DirectionName}】`
  }

  const body = (type?: string) => (
    <>
      <div className="search_condition_title">
        {
          sortKey && sortDirection ? (
            <>
              絞り込み・ソート条件
            </>
          ) : (
            <>
              絞り込み条件
            </>
          )
        }
        {
          type ? (
            <></>
          ) : (
            <SearchModule
              callbackKeywords={callbackKeywords}
              callbackLabelMasterKeywords={callbackLabelMasterKeywords}
              callbackKey={callbackKey}
              callbackDirection={callbackDirection}
              callbackDisabled={callbackDisabled}
              callbackPageOffset={callbackPageOffset}
              callbackMemberIds={callbackMemberIds}
              callbackTeamIds={callbackTeamIds}
              callbackLabelGroupIds={callbackLabelGroupIds}
              callbackMasterRelationLabelGroupIds={callbackMasterRelationLabelGroupIds}
              callbackMasterRelationLabelGroupDisabled={callbackMasterRelationLabelGroupDisabled}
              callbackColorLabels={callbackColorLabels}
              callbackCreatorMemberIds={callbackCreatorMemberIds}
              callbackViewerMemberIds={callbackViewerMemberIds}
              callbackUserMemberIds={callbackUserMemberIds}
              memberList={memberList}
              teamList={teamList}
              labelGroupList={labelGroupList}
              masterRelationLabelGroupList={masterRelationLabelGroupList}
            />
          )
        }
      </div>
      <div className="chip_wrap">
        {
          date ? (
            <div className="chip_area">
              <div>
                期間：
              </div>
              <div className="chip">
                {startOfMonth(date, "YYYY-MM-DD")}
                &nbsp;~&nbsp;
                {endDate}
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          keywords && keywords.length ? (
            <div className="chip_area">
              <div>
                プロジェクト名・プロジェクトコード：
              </div>
              <div className="chip_list">
                <div className="chip">
                  {`「${keywords.join(",")}」を含むプロジェクト`}
                </div>
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          labelMasterKeywords && labelMasterKeywords.length ? (
            <div className="chip_area">
              <div>
                業務名：
              </div>
              <div className="chip_list">
                <div className="chip">
                  {`「${labelMasterKeywords.join(",")}」を含む業務名`}
                </div>
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          teamList?.length && teamIds && teamIds.length ? (
            <div className="chip_area">
              <div>
                チーム：
              </div>
              <div className="chip_list">
                {
                  teamList?.filter((team) => teamIds.includes(team.id)).map((team) => (
                    <div
                      key={`search_condition_team_${team.id}`}
                      className="chip"
                    >
                      {team.name}
                    </div>
                  ))
                }
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          memberList?.length && memberIds && memberIds.length ? (
            <div className="chip_area">
              <div>
                メンバー：
              </div>
              <div className="chip_list">
                {
                  memberList.filter((member) => memberIds.includes(member.id)).map((member) => (
                    <div
                      key={`search_condition_member_${member.id}`}
                      className="chip"
                    >
                      {member.name}
                    </div>
                  ))
                }
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          labelGroupList?.length && labelGroupIds && labelGroupIds.length ? (
            <div className="chip_area">
              <div>
                プロジェクト：
              </div>
              <div className="chip_list">
                {
                  labelGroupList.filter((labelGroup) => labelGroupIds.includes(labelGroup.id)).map((labelGroup) => (
                    <div
                      key={`search_condition_project_${labelGroup.id}`}
                      className="chip"
                    >
                      {labelGroup.name}
                    </div>
                  ))
                }
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          masterRelationLabelGroupList?.length
            && masterRelationLabelGroupIds
            && masterRelationLabelGroupIds.length
            && !masterRelationLabelGroupDisabled ? (
              <div className="chip_area">
                <div>
                  プロジェクト：
                </div>
                <div className="chip_list">
                  {
                    masterRelationLabelGroupList.filter((labelGroup) => masterRelationLabelGroupIds.includes(labelGroup.id)).map((labelGroup) => (
                      <div
                        key={`search_condition_project_${labelGroup.id}`}
                        className="chip"
                      >
                        {labelGroup.name}
                      </div>
                    ))
                  }
                </div>
              </div>
          ) : (
            <></>
          )
        }
        {
          masterRelationLabelGroupDisabled && (
            <div className="chip_area">
              <div>
                プロジェクト：プロジェクトに紐づいていない業務のみ表示
              </div>
            </div>
          )
        }
        {
          disabled !== undefined ? (
            <div className="chip_area">
              <div>
                プロジェクトの状態：
              </div>
              <div className="chip">
                {disabled ? "完了" : "未完了"}
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          sortKey && sortDirection ? (
            <div className="chip_area">
              <div>
                ソート：
              </div>
              <div className="chip">
                {buildSortValue()}
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          memberList?.length && creatorMemberIds && creatorMemberIds.length ? (
            <div className="chip_area">
              <div>
                作成者：
              </div>
              <div className="chip_list">
                {
                  memberList.filter((member) => creatorMemberIds.includes(member.id)).map((member) => (
                    <div
                      key={`search_condition_creator_member_${member.id}`}
                      className="chip"
                    >
                      {member.name}
                    </div>
                  ))
                }
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          memberList?.length && viewerMemberIds && viewerMemberIds.length ? (
            <div className="chip_area">
              <div>
                表示メンバー：
              </div>
              <div className="chip_list">
                {
                  memberList.filter((member) => viewerMemberIds.includes(member.id)).map((member) => (
                    <div
                      key={`search_condition_viewer_member_${member.id}`}
                      className="chip"
                    >
                      {member.name}
                    </div>
                  ))
                }
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          memberList?.length && userMemberIds && userMemberIds.length ? (
            <div className="chip_area">
              <div>
                参加メンバー：
              </div>
              <div className="chip_list">
                {
                  memberList.filter((member) => userMemberIds.includes(member.id)).map((member) => (
                    <div
                      key={`search_condition_user_member_${member.id}`}
                      className="chip"
                    >
                      {member.name}
                    </div>
                  ))
                }
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          colors?.length ? (
            <div className="chip_area">
              <div>
                プロジェクトカラー：
              </div>
              <div className="chip_list">
                {
                  COLOR_LIST.map((v) => v.values).flat().filter((v) => colors.includes(v.label)).map((color) => (
                    <div
                      key={`search_condition_color_${color.label}`}
                      className="chip"
                    >
                      <div
                        style={{
                          backgroundColor: color.color,
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                        }}
                      />
                      {color.text}
                    </div>
                  ))
                }
              </div>
            </div>
          ) : (
            <></>
          )
        }
        {
          !date
          && !keywords?.length
          && !labelMasterKeywords?.length
          && !sortKey
          && !sortDirection
          && !disabled
          && !teamIds?.length
          && !memberIds?.length
          && !labelGroupIds?.length
          && !masterRelationLabelGroupIds?.length
          && !masterRelationLabelGroupDisabled
          && !colors?.length
          && !creatorMemberIds?.length
          && !viewerMemberIds?.length
          && !userMemberIds?.length
          && (
            <>
              絞り込み・ソート条件は指定されていません
            </>
          )
        }
      </div>
    </>
  )

  useEffect(() => {
    if (!login.accessToken) return undefined
    document.addEventListener("scroll", onScroll)
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
    return () => document.removeEventListener("scroll", onScroll)
  }, [login.accessToken])

  return (
    <>
      <section className={`sticky_header ${isDisplaySticky && "active"}`}>
        <div style={{ width: "100%" }}>
          <div className="sticky_header_title">
            {name}
          </div>
          {body("head")}
        </div>
      </section>
      <section className="search_condition">
        {body()}
      </section>
    </>
  )
}

SearchConditionModule.defaultProps = {
  date: undefined,
  teamIds: undefined,
  memberIds: undefined,
  keywords: undefined,
  labelMasterKeywords: undefined,
  sortKey: undefined,
  sortDirection: undefined,
  disabled: undefined,
  labelGroupIds: undefined,
  masterRelationLabelGroupIds: undefined,
  masterRelationLabelGroupDisabled: undefined,
  colors: undefined,
  creatorMemberIds: undefined,
  viewerMemberIds: undefined,
  userMemberIds: undefined,
  callbackTeamIds: undefined,
  callbackMemberIds: undefined,
  callbackKeywords: undefined,
  callbackLabelMasterKeywords: undefined,
  callbackKey: undefined,
  callbackDirection: undefined,
  callbackDisabled: undefined,
  callbackPageOffset: undefined,
  callbackLabelGroupIds: undefined,
  callbackMasterRelationLabelGroupIds: undefined,
  callbackMasterRelationLabelGroupDisabled: undefined,
  callbackColorLabels: undefined,
  callbackCreatorMemberIds: undefined,
  callbackViewerMemberIds: undefined,
  callbackUserMemberIds: undefined,
  memberList: undefined,
  teamList: undefined,
  labelGroupList: undefined,
  masterRelationLabelGroupList: undefined,
}

export default SearchConditionModule
